import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from "@/components/ui/table";
import {
  CurrencyInr, ArrowDown, CheckCircle, XCircle, Warning, Minus,
  CaretDown, CaretRight
} from "@phosphor-icons/react";

const DECISION_CONFIG = {
  approved: { label: "APPROVED", color: "bg-green-100 text-green-800 border-green-300", icon: CheckCircle },
  approved_with_observations: { label: "APPROVED WITH OBSERVATIONS", color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: Warning },
  partial_approval: { label: "PARTIAL APPROVAL", color: "bg-orange-100 text-orange-800 border-orange-300", icon: Warning },
  rejected: { label: "REJECTED", color: "bg-red-100 text-red-800 border-red-300", icon: XCircle }
};

function NmeDeductionRow({ ded, idx, fmt }) {
  const [expanded, setExpanded] = useState(false);
  const hasNmeItems = ded.nme_items && ded.nme_items.length > 0;
  const isNme = ded.type?.includes("NME") || ded.type?.includes("Non-Medical");

  return (
    <div data-testid={`deduction-${idx}`}>
      <div
        className={`px-4 py-2.5 flex items-center justify-between ${isNme && hasNmeItems ? "cursor-pointer hover:bg-red-50/50" : ""}`}
        onClick={() => isNme && hasNmeItems && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isNme && hasNmeItems ? (
            expanded ? <CaretDown size={14} className="text-red-400 flex-shrink-0" /> : <CaretRight size={14} className="text-red-400 flex-shrink-0" />
          ) : (
            <Minus size={14} className="text-red-500 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <span className="text-sm text-gray-700">{ded.type}</span>
            <p className="text-xs text-gray-500">{ded.reason}</p>
          </div>
        </div>
        <span className="text-sm font-medium text-red-600 flex-shrink-0">- {fmt(ded.amount)}</span>
      </div>

      {/* Expanded NME item detail */}
      {isNme && hasNmeItems && expanded && (
        <div className="px-4 pb-3">
          <div className="border border-red-200 rounded-md overflow-hidden bg-red-50/30 ml-6">
            <div className="bg-red-50 px-3 py-1.5 border-b border-red-200">
              <p className="text-[10px] font-semibold text-red-700 uppercase tracking-wider">
                {ded.nme_items.length} Non-Payable Items Identified
              </p>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-red-100 bg-red-50/50">
                  <th className="px-3 py-1.5 text-left font-semibold text-red-700 uppercase tracking-wider">#</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-red-700 uppercase tracking-wider">Item</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-red-700 uppercase tracking-wider">Category</th>
                  <th className="px-3 py-1.5 text-right font-semibold text-red-700 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ded.nme_items.map((item, i) => (
                  <tr key={i} className="border-b border-red-100 last:border-0 hover:bg-red-50/50">
                    <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-1.5 text-gray-800 font-medium max-w-[200px] truncate">{item.item}</td>
                    <td className="px-3 py-1.5">
                      <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200">{item.category}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right text-red-600 font-medium">{fmt(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-red-200 bg-red-50">
                  <td colSpan={3} className="px-3 py-1.5 text-right font-semibold text-red-800 text-xs">Total NME Deduction</td>
                  <td className="px-3 py-1.5 text-right font-bold text-red-700">{fmt(ded.amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


export default function FinalAmountTab({ claim }) {
  const finalAmount = claim.final_amount || {};
  const tariffResults = claim.tariff_results || {};
  const isProcessed = claim.status === "processed";

  if (!isProcessed || !finalAmount.claimed_amount) {
    return (
      <div className="p-6 text-center text-gray-400" data-testid="final-empty">
        <CurrencyInr size={48} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm">Process the claim to view the final amount calculation.</p>
      </div>
    );
  }

  const fmt = (n) => `INR ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  const decision = DECISION_CONFIG[finalAmount.decision] || DECISION_CONFIG.approved_with_observations;
  const DecisionIcon = decision.icon;

  return (
    <div className="p-6 space-y-6" data-testid="final-amount-tab">
      {/* Decision Banner */}
      <div className={`border-2 rounded-md p-5 flex items-center justify-between ${decision.color}`} data-testid="decision-banner">
        <div className="flex items-center gap-3">
          <DecisionIcon size={28} weight="fill" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Decision</p>
            <p className="text-lg font-bold">{decision.label}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Net Payable</p>
          <p className="text-3xl font-bold" data-testid="final-net-payable">{fmt(finalAmount.net_payable)}</p>
        </div>
      </div>

      {/* Amount Breakdown */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3">Amount Breakdown</h3>
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">Total Claimed Amount</span>
            <span className="text-lg font-bold text-gray-900" data-testid="final-claimed">{fmt(finalAmount.claimed_amount)}</span>
          </div>

          <div className="px-4 py-3 flex items-center justify-between border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">After Tariff Adjustment</span>
            <span className="text-base font-semibold text-gray-800">{fmt(finalAmount.approved_amount)}</span>
          </div>

          {/* Deductions */}
          {(finalAmount.deductions || []).length > 0 && (
            <div className="divide-y divide-gray-100">
              {finalAmount.deductions.map((ded, idx) => (
                <NmeDeductionRow key={idx} ded={ded} idx={idx} fmt={fmt} />
              ))}
            </div>
          )}

          <div className="bg-[#002FA7]/5 px-4 py-3 flex items-center justify-between border-t-2 border-[#002FA7]">
            <span className="text-sm font-bold text-[#002FA7]">NET PAYABLE AMOUNT</span>
            <span className="text-xl font-bold text-[#002FA7]" data-testid="final-net-payable-bottom">{fmt(finalAmount.net_payable)}</span>
          </div>
        </div>
      </section>

      {/* Sum Insured Info */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3">Sum Insured Status</h3>
        <div className="border border-gray-200 rounded-md p-4 grid grid-cols-3 gap-4">
          <div>
            <p className="label-xs mb-1">Sum Insured</p>
            <p className="text-lg font-bold text-gray-900">{fmt(finalAmount.sum_insured)}</p>
          </div>
          <div>
            <p className="label-xs mb-1">This Claim</p>
            <p className="text-lg font-bold text-[#002FA7]">{fmt(finalAmount.net_payable)}</p>
          </div>
          <div>
            <p className="label-xs mb-1">Remaining</p>
            <p className="text-lg font-bold text-green-700">{fmt(finalAmount.sum_insured_remaining)}</p>
          </div>
        </div>
      </section>

      {/* Tariff Adjustments */}
      {(tariffResults.adjustments || []).length > 0 && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Tariff Adjustments</h3>
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {tariffResults.is_network_hospital ? "Network Hospital" : "Non-Network Hospital"}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">
                Plan: {tariffResults.plan_type}
              </Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Item</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Claimed</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Approved</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Adjustment</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tariffResults.adjustments.map((adj, idx) => (
                  <TableRow key={idx} className={adj.adjustment > 0 ? "bg-red-50/50" : ""}>
                    <TableCell className="text-sm text-gray-900 font-medium max-w-[150px] truncate">{adj.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">{adj.service_type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 text-right">{fmt(adj.claimed_amount)}</TableCell>
                    <TableCell className="text-sm font-medium text-gray-900 text-right">{fmt(adj.approved_amount)}</TableCell>
                    <TableCell className={`text-sm text-right font-medium ${adj.adjustment > 0 ? "text-red-600" : "text-gray-400"}`}>
                      {adj.adjustment > 0 ? `- ${fmt(adj.adjustment)}` : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 max-w-[200px] truncate">{adj.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}
    </div>
  );
}
